// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The two hooks the arcade and the play route read machines through (G1-04 … G1-09).
//
// **Every reach for the network in this file is a dynamic `import('./account')`**, which is the
// boundary `scripts/check-anonymous.mjs` stops its walk at. Nothing here is imported eagerly, so a
// visitor on the front door downloads none of it, and — the part that matters more — `useMachine`
// resolves the bundled flagship and *returns* before the import is named at all. A stranger opening
// a shared link to NEON NOVA makes zero requests whether or not they have ever signed in.
//
// The resolution itself is `machineSource.ts`, which is pure and injected. This file is the wiring:
// it owns the effect, the cancellation and the four states a fetch has on screen, and it owns none
// of the decisions.

import { useEffect, useState } from 'react'
import { preparePackagedArtwork } from '../world/packagedIllustrations'
import {
  BUNDLED_MACHINE_ID,
  bundledMachine,
  openableRoster,
  resolveMachine,
  type MachineOutcome,
  type RosterMachine,
} from './machineSource'
import { openMachineCache } from '../storage/machineCache'
import { stockMachine } from './stockMachines'

/** What the arcade knows about the catalogue at this moment. */
export interface RosterState {
  /**
   * The published machines, or nothing when they could not be read.
   *
   * `undefined` is *not yet, or not at all* and never "there are none". An empty list would make
   * every machine unknown, which is a 404 for links that are perfectly good.
   */
  readonly roster: readonly RosterMachine[] | undefined
  /** The week's free machine by slug, `null` for none, `undefined` for not known. */
  readonly freeMachineSlug: string | null | undefined
  /** True while the first answer is still outstanding, so a page can show a skeleton. */
  readonly loading: boolean
}

/**
 * The roster and the week's free machine.
 *
 * Both in one hook because the arcade needs both to draw a single tile correctly — the slug decides
 * whether a tile is badged `free-this-week` or `needs-pro` — and two hooks would give the page two
 * moments at which a badge changes under the reader.
 *
 * **`loading` goes false when both have answered, including when both failed.** A page that stayed
 * in its skeleton state after a failure is a page that never says what went wrong.
 *
 * `enabled` is what keeps the arcade inside the anonymous funnel. See the caller: a browser that has
 * never signed in asks for nothing at all, and this hook does not run.
 */
export function useRoster(enabled: boolean): RosterState {
  const [roster, setRoster] = useState<readonly RosterMachine[] | undefined>(undefined)
  const [freeMachineSlug, setFree] = useState<string | null | undefined>(undefined)
  const [loading, setLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    let alive = true
    void (async () => {
      try {
        const feed = await import('./account')
        const [rows, slug] = await Promise.all([feed.fetchRoster(), feed.fetchFreeMachineSlug()])
        if (!alive) return
        setRoster(rows)
        setFree(slug)
      } catch {
        // A chunk that would not load is the same to this page as a request that did not answer:
        // there is a bundled machine either way, and the arcade falls back to it.
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [enabled])

  return { roster, freeMachineSlug, loading }
}

/** What the play route knows about one machine at this moment. */
export interface MachineState {
  /** The answer, or nothing while it is still being worked out. */
  readonly outcome: MachineOutcome | undefined
  /** True until there is an outcome, so the stage can show a skeleton rather than nothing. */
  readonly loading: boolean
}

/**
 * Resolve one machine id to a definition or to a refusal that names itself.
 *
 * **The flagship short-circuits synchronously, before any import.** Written as a lazy initial state
 * rather than as an effect that sets it, so the first render of `/play/neon-nova` already has the
 * machine: an effect would give one frame of skeleton to the one route that is supposed to be
 * instant, and that frame is the whole of 08 §1's "instant NEON NOVA play".
 */
export function useMachine(machineId: string | undefined): MachineState {
  const [outcome, setOutcome] = useState<MachineOutcome | undefined>(() =>
    machineId === BUNDLED_MACHINE_ID ? bundledMachine() : undefined,
  )

  useEffect(() => {
    if (machineId === undefined) {
      setOutcome({ kind: 'refused', reason: 'unknown', machineId: '', detail: '' })
      return
    }
    if (machineId === BUNDLED_MACHINE_ID) {
      setOutcome(bundledMachine())
      return
    }
    let alive = true
    setOutcome(undefined)
    void (async () => {
      try {
        const feed = await import('./account')
        const cache = await openMachineCache()
        // Fixed stock identity comes from this build's catalogue. A missing online publish row
        // must never turn one of the six advertised stock cards into a 404.
        const roster = stockMachine(machineId) === undefined ? await feed.fetchRoster() : undefined
        const source = {
          stockAccess: feed.stockMachineAccess,
          cached: (hash: string) => cache.get(hash),
          store: (hash: string, definition: unknown, id: string, semver: string) =>
            cache.put(hash, definition, id, semver),
          fetchVersion: feed.fetchDefinition,
        }
        const found = await resolveMachine(machineId, { ...source, roster })
        if (found.kind === 'ok') await preparePackagedArtwork(found.definition)
        /**
         * **The author's own machines, asked for only when the roster has already said no**
         * (M10-26).
         *
         * `GET /machines` is the published roster and cannot answer this: it is public, and it
         * joins each machine to its newest *published* version. So a draft appears in no roster,
         * and every link to one — from the Builder's own list, from an author's bookmark — resolved
         * to `unknown` and drew "no machine called that" at somebody looking at their own work.
         *
         * **A fallback rather than a merge**, which is the difference between one extra request in
         * the case that is currently a dead end and one on every machine page for every signed-in
         * player. `fetchMyMachines` answers `undefined` with no session and sends nothing.
         *
         * **Only `unknown`.** A `needs-pro` is a machine that exists and this viewer may not have,
         * and asking a second question about it would turn a paywall into a 404; `offline` means
         * nothing could be read at all, including this. The one refusal worth a second opinion is
         * the one that means "no such machine", because for an author it may be wrong.
         */
        if (found.kind === 'refused' && found.reason === 'unknown') {
          const owned = await feed.fetchMyMachines()
          if (owned !== undefined && owned.length > 0) {
            const mine = await resolveMachine(machineId, {
              ...source,
              roster: openableRoster(owned),
            })
            // The first answer stands unless the second one is better. A machine that is not the
            // author's own is `unknown` either way, and re-reporting the second attempt's refusal
            // would be answering a question nobody asked.
            if (mine.kind === 'ok') await preparePackagedArtwork(mine.definition)
            if (alive) setOutcome(mine.kind === 'ok' ? mine : found)
            return
          }
        }
        if (alive) setOutcome(found)
      } catch {
        if (alive) setOutcome({ kind: 'refused', reason: 'offline', machineId, detail: '' })
      }
    })()
    return () => {
      alive = false
    }
  }, [machineId])

  return { outcome, loading: outcome === undefined }
}
